import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS, MOBILE, REDUCED_MOTION } from '@/nova/theme/tokens';

/* The "What do you want to advertise?" chooser: three category cards (Promotions / Games / Gamification).
   Each card is a single cover illustration, dimmed to 25% (kept coloured) at rest and lifted to 50% on
   hover with a subtle zoom. The illustration's transparent lower-left keeps the label legible, no scrim.
   Colours/timings come only from theme tokens. */

/* One column per offered category, each an equal fraction so the cards fill the sheet width (no left/right
   gaps) whether two or three categories are offered. */
export const Grid = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$count}, minmax(0, 1fr));
  gap: 14px;

  ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

/** The illustration covers the card, anchored top-right (its transparent lower-left keeps the label clear).
 *  Dimmed to 25% (still coloured) at rest, lifted to 50% on hover with a subtle zoom. */
export const Art = styled.img`
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 75% top;
  pointer-events: none;
  user-select: none;
  opacity: 0.75;
  transform: scale(1.02);
  transition:
    transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

export const Card = styled.button`
  position: relative;
  overflow: hidden;
  min-height: 464px;
  border-radius: 16px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: left;
  padding: 0;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover ${Art} {
    opacity: 1;
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }

  /* Reduced motion: keep the brightness reveal, drop the zoom. */
  ${REDUCED_MOTION} {
    &:hover ${Art} {
      transform: scale(1.02);
    }
  }
`;

export const Label = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
`;

export const Name = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 19px;
  font-weight: 700;
  color: ${COLORS.text};
`;

export const Desc = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${COLORS.textMuted};
  max-width: 24ch;
`;
